import React, { ReactElement, useCallback, useMemo } from 'react';
import { Col, FlexboxGrid, Text } from 'rsuite';
import FormTextField from '../../form/FormTextField';
import { FORM_INPUT_TYPES, INPUT_TYPE } from '../../../common/enums';
import { BucketItem } from './BucketItem';
import { BucketError, RevenueReport } from 'src/types/tax';
import { FormTextFieldProps } from '../../form/FormTextField/FormTextField';
import { roundDecimalPlaces } from '../../../common/utils';
import { TaxConfigurationDeductions } from 'src/types/configuration';
export interface BucketProps {
  error?: BucketError;
  name: string;
  title: string;
  value?: RevenueReport;
  isMobile?: boolean;
  deductionsConfig?: TaxConfigurationDeductions;
}

export const Bucket = ({
  error,
  name,
  title,
  value,
  isMobile,
  deductionsConfig,
}: BucketProps): ReactElement => {
  const totalLabel = 'Total Receipts';
  const revenueLabel =
    'Total Revenue for Room Rentals, cleaning fees, pet fees etc.';
  const deductionsLabel =
    deductionsConfig?.label ??
    'Allowable Deductions: Federal government employees and 30+ day rentals.';
  const total = useMemo(() => {
    if (value?.revenue) {
      if (value?.discounts) {
        return roundDecimalPlaces(
          Number(value?.revenue) - Number(value?.discounts)
        );
      }
      return roundDecimalPlaces(Number(value?.revenue));
    }
    return 0;
  }, [value?.revenue, value?.discounts]);

  const getInputPadding = useCallback(
    (error: string | undefined) => {
      if (error) {
        return '0';
      }
      if (isMobile) {
        return '20px';
      }
      return '40px';
    },
    [isMobile]
  );

  const commonFieldProps: Partial<FormTextFieldProps> = useMemo(
    () => ({
      inputFieldType: INPUT_TYPE.TEXT,
      inputMode: 'decimal',
      inputGroupAddOnStart: '$',
      trimEndOnChange: true,
      trimStartOnChange: true,
      type: FORM_INPUT_TYPES.CURRENCY,
    }),
    []
  );

  return (
    <FlexboxGrid style={{ padding: !isMobile ? '20px 0' : ' 6px 0' }}>
      <FlexboxGrid.Item
        colspan={24}
        as={Col}
        style={{ padding: '0 12px 6px' }}
      >
        <Text as="h5">{title}</Text>
      </FlexboxGrid.Item>
      <FlexboxGrid.Item
        colspan={24}
        as={Col}
      >
        <BucketItem
          dark
          label={revenueLabel}
        >
          <FormTextField
            {...commonFieldProps}
            errorMessage={error?.revenue}
            hasError={!!error?.revenue}
            key={`${name}.revenue`}
            name={`${name}.revenue`}
            value={value?.revenue}
            style={{
              textAlign: 'right',
              paddingRight: getInputPadding(error?.revenue),
            }}
          />
        </BucketItem>
        {deductionsConfig?.enable && (
          <BucketItem
            description={deductionsConfig?.text}
            label={deductionsLabel}
          >
            <FormTextField
              {...commonFieldProps}
              errorMessage={error?.discounts}
              hasError={!!error?.discounts}
              key={`${name}.discounts`}
              name={`${name}.discounts`}
              value={value?.discounts}
              style={{
                textAlign: 'right',
                paddingRight: getInputPadding(error?.discounts),
              }}
            />
          </BucketItem>
        )}
        <BucketItem
          dark={deductionsConfig?.enable}
          label={totalLabel}
        >
          <div
            style={{
              paddingLeft: '12px',
              paddingRight: isMobile ? '22px' : '42px',
              display: 'flex',
              justifyContent: 'space-between',
              gap: '5',
            }}
          >
            ${' '}
            <span
              style={{ display: 'block' }}
              className="w-full align-right"
            >
              {total}
            </span>
          </div>
        </BucketItem>
      </FlexboxGrid.Item>
    </FlexboxGrid>
  );
};
